// /onay/:reference — editorial confirmation with embedded PDF preview.

import { SiteHeader } from '@/components/SiteHeader.tsx';
import { ApiRequestError, api } from '@/lib/api.ts';
import { LEVEL_LABELS_TR } from '@okn/shared';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

type ReservationLookup = {
  reservation: {
    referenceCode: string;
    fullName: string;
    guardianName?: string | null;
    email: string | null;
    phone: string;
    seatCount: number;
    status: 'reserved' | 'paid' | 'cancelled' | 'no_show';
    createdAt: string;
    documentUrl?: string | null;
    pdfUrl?: string | null;
    emailSentAt?: string | null;
    approvalDeadline?: string | null;
    eventName?: string;
    eventStartsAt?: string;
  };
  seats: Array<{ id: string; label: string; level: string; rowLabel: string; seatNumber: number }>;
};

function formatDeadline(iso: string): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    dateStyle: 'long',
    timeStyle: 'short',
  })
    .format(new Date(iso))
    .replace(/(\d{1,2}):(\d{2})/g, '$1.$2');
}

export function Confirmation() {
  const { reference = '' } = useParams<{ reference: string }>();
  const [data, setData] = useState<ReservationLookup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    if (!reference) return;
    let cancelled = false;
    const fetchOnce = () =>
      api<ReservationLookup>(`/reservations/${encodeURIComponent(reference)}`)
        .then((d) => {
          if (cancelled) return;
          setData(d);
          // If PDF isn't ready yet, poll briefly until it appears.
          if (!d.reservation.pdfUrl && refreshNonce < 8) {
            setTimeout(() => setRefreshNonce((n) => n + 1), 1200);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          if (err instanceof ApiRequestError) setError(err.body.error.message);
          else setError('Rezervasyon bilgisi alınamadı');
        });
    fetchOnce();
    return () => {
      cancelled = true;
    };
  }, [reference, refreshNonce]);

  if (error) {
    return (
      <div className="min-h-dvh">
        <SiteHeader />
        <main className="mx-auto flex max-w-md flex-col justify-center px-8 py-24">
          <div className="kicker text-accent">Hata</div>
          <h1 className="display mt-3 text-3xl font-light tracking-tight">{error}</h1>
          <Link to="/" className="mt-8 text-[13px] text-text-muted hover:text-text">
            ← Ana sayfa
          </Link>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-dvh">
        <SiteHeader />
        <main className="mx-auto flex max-w-md flex-col justify-center px-8 py-24">
          <p className="display text-2xl italic text-text-muted">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  const { reservation, seats } = data;

  return (
    <div className="min-h-dvh">
      <SiteHeader />

      <main className="mx-auto grid max-w-[1500px] grid-cols-1 gap-10 px-8 py-12 lg:grid-cols-[1.05fr_1fr]">
        {/* LEFT — reservation info, hero ref code, actions */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col"
        >
          <div className="kicker mb-3 text-accent">
            <span className="inline-block h-px w-8 align-middle bg-accent mr-3" />
            Ön Rezervasyon yapıldı
          </div>

          <h1 className="display text-[clamp(36px,4.5vw,64px)] leading-[0.95] font-light tracking-tight">
            Hoş geldiniz,
            <br />
            <em className="italic text-accent">{reservation.fullName.split(' ')[0]}</em>.
          </h1>

          <div className="mt-6 rounded-xl border border-accent/30 bg-accent-faint px-5 py-4">
            <div className="kicker mb-2 text-accent">Önemli</div>
            <p className="text-[14px] leading-relaxed text-text">
              Rezervasyonunuzun <strong>gerçekleşmesi</strong> için{' '}
              {reservation.approvalDeadline ? (
                <>
                  <strong className="tnum">{formatDeadline(reservation.approvalDeadline)}</strong>
                  'a kadar
                </>
              ) : (
                <strong>en kısa zamanda</strong>
              )}{' '}
              <strong>Acar Sanat Akademi</strong>'de onay görüşmesi gerçekleştirmeniz gerekmektedir.
            </p>
          </div>

          <p className="mt-5 max-w-md text-[14px] leading-relaxed text-text-muted">
            Görüşme sırasında <span className="text-text">referans kodunuzu</span> ve kimliğinizi
            yanınızda bulundurmanız yeterli olacaktır.
          </p>

          {/* Reference code hero */}
          <motion.div
            layoutId="ref-code"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 220, damping: 24 }}
            className="mt-10 inline-flex w-fit flex-col rounded-xl border border-border-strong bg-bg-elevated px-7 py-5"
          >
            <div className="kicker mb-2">Referans</div>
            <div className="mono text-[40px] font-medium leading-none tracking-tight text-accent">
              {reservation.referenceCode}
            </div>
          </motion.div>

          {/* Status + actions */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <StatusPill status={reservation.status} />
            <span className="kicker text-text-faint">Bilgilendirme WhatsApp ile yapılır</span>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {reservation.pdfUrl ? (
              <a
                href={reservation.pdfUrl}
                download={`${reservation.referenceCode}.pdf`}
                className="group inline-flex items-center gap-3 rounded-md bg-accent px-5 py-3 text-[13px] font-medium tracking-wide text-white transition-all hover:bg-accent-hover hover:shadow-[0_8px_24px_-8px_var(--accent)]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M12 4v12m0 0l-5-5m5 5l5-5M4 20h16" />
                </svg>
                Rezervasyon Kartını İndir
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-5 py-3 text-[13px] text-text-faint"
              >
                <span className="block h-3 w-3 animate-spin rounded-full border border-text-faint border-t-transparent" />
                Rezervasyon Kartı hazırlanıyor
              </button>
            )}
            {reservation.documentUrl && (
              <a
                href={reservation.documentUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-4 py-3 text-[13px] text-text-muted transition-colors hover:border-border-strong hover:text-text"
              >
                Tarayıcıda aç ↗
              </a>
            )}
          </div>

          {/* Seat list */}
          <section className="mt-10 overflow-hidden rounded-xl border border-border bg-bg-elevated">
            <header className="flex items-center justify-between border-b border-border px-5 py-3">
              <div className="kicker">Koltuklar</div>
              <div className="display text-sm italic text-text-muted">
                {reservation.seatCount} adet
              </div>
            </header>
            <ul className="divide-y divide-border">
              {seats.map((s, i) => (
                <motion.li
                  key={s.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + i * 0.04 }}
                  className="flex items-center justify-between gap-3 px-5 py-3.5"
                >
                  <div className="flex items-center gap-3">
                    <div className="display flex h-9 w-9 items-center justify-center rounded-md border border-border-strong text-[12px] text-accent">
                      {s.rowLabel}
                    </div>
                    <div>
                      <div className="mono text-[14px]">{s.label}</div>
                      <div className="text-[11px] text-text-faint">
                        {LEVEL_LABELS_TR[s.level as keyof typeof LEVEL_LABELS_TR] ?? s.level}
                      </div>
                    </div>
                  </div>
                  <div className="kicker text-[10px]">Sıra {s.rowLabel}</div>
                </motion.li>
              ))}
            </ul>
          </section>

          {/* Contact recap */}
          <section className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3">
            <Cell label="Veli" value={reservation.guardianName ?? '—'} />
            <Cell label="E-posta" value={reservation.email ?? '—'} />
            <Cell label="Telefon" value={reservation.phone} />
          </section>

          <Link
            to="/"
            className="mt-10 inline-block self-start text-[12px] uppercase tracking-[0.22em] text-text-faint transition-colors hover:text-text"
          >
            ← Ana sayfa
          </Link>
        </motion.section>

        {/* RIGHT — embedded rezervasyon kartı preview */}
        <motion.aside
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:sticky lg:top-8 lg:self-start"
        >
          <div className="overflow-hidden rounded-2xl border border-border-strong bg-bg-elevated shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div className="kicker">Rezervasyon Kartı</div>
              <div className="flex items-center gap-1.5 text-[11px] text-text-faint">
                <span className="block h-1.5 w-1.5 rounded-full bg-accent" />
                Canlı
              </div>
            </div>
            {reservation.documentUrl ? (
              <iframe
                key={reservation.documentUrl}
                src={reservation.documentUrl}
                title="Rezervasyon belgesi"
                className="h-[820px] w-full bg-white"
              />
            ) : (
              <div className="flex h-[820px] items-center justify-center text-text-faint">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-6 w-6 animate-spin rounded-full border border-text-faint border-t-transparent" />
                  <div className="kicker">Hazırlanıyor</div>
                </div>
              </div>
            )}
          </div>
        </motion.aside>
      </main>
    </div>
  );
}

function StatusPill({ status }: { status: ReservationLookup['reservation']['status'] }) {
  const config = {
    reserved: { label: 'Ön Rezervasyon', bg: 'bg-gold-quiet', dot: 'bg-gold', text: 'text-gold' },
    paid: {
      label: 'Onaylandı',
      bg: 'bg-[rgba(106,168,79,0.14)]',
      dot: 'bg-success',
      text: 'text-success',
    },
    cancelled: { label: 'İptal', bg: 'bg-accent-faint', dot: 'bg-accent', text: 'text-accent' },
    no_show: { label: 'Gelmedi', bg: 'bg-surface', dot: 'bg-text-faint', text: 'text-text-muted' },
  }[status];

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em] ${config.bg} ${config.text}`}
    >
      <span className={`block h-1.5 w-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-bg-elevated px-4 py-3">
      <div className="kicker">{label}</div>
      <div className="mt-1.5 text-[13px] text-text break-words">{value}</div>
    </div>
  );
}
